// Decodes a real IFC file, entirely in the browser, via the vendored ifc-mesh-wasm engine (see
// src/vendor/ifc-mesh-wasm/NOTICE.md) into Three.js geometry plus a flat element list.
//
// Adapted from aec-project-controls/src/domain/ifcModelImport.ts. That app's `ModelObject` type
// carries scheduling fields (taskId, linkState) that belong to Controls, not CDE — CDE's product
// boundary is opaque, display-only model viewing (plan/product-boundary.md: "モデルはopaqueで
// immutableなversion referenceとして読む"), so this port defines its own minimal
// `ImportedIfcElement` instead of reusing that type, to avoid quietly implying CDE tracks
// schedule/link state it does not.
//
// Geometry stays in the engine's own Z-up, millimetre world space (mm / 1000 -> metres, no axis
// swap) — the same convention ifc-engine-core's own reference viewer uses.
//
// Rendering is one mesh PER CHUNK, not per element: a real building can carry thousands of
// elements per chunk, and a mesh + material per element means thousands of draw calls and a
// per-frame material-color loop over all of them — exactly the "gets slower, visual artifacts"
// failure a large real file (432K triangles, BLCJ Archicad sample) surfaced in Controls' own
// history. Chunk count stays in the dozens even for a large model (ifc-mesh-wasm buckets by
// storey), so per-chunk rendering is the actual engine-native scale unit. Picking and selection
// highlighting resolve an individual element from the chunk's own instanceIndex buffer instead of
// needing a separate mesh per element (see components/IfcModelViewport.tsx).

import * as THREE from "three";
import { decodeChunkHeader, VERTEX_STRIDE_BYTES, WasmMeshSource } from "./ifcMeshSource";

export type ImportedIfcStorey = {
  level: number;
  name: string | null;
  elementCount: number;
};

/** A flat, opaque description of one IFC element — display metadata only, no schedule/link state. */
export type ImportedIfcElement = {
  id: string;
  name: string;
  ifcClass: string | null;
  level: string;
  position: readonly [number, number, number];
  size: readonly [number, number, number];
};

/** One renderable chunk plus what picking and selection highlighting need. */
export type ImportedChunk = {
  chunkId: number;
  geometry: THREE.BufferGeometry;
  /** Parallel to `geometry`'s position attribute — vertex i belongs to instanceByVertex[i]. */
  instanceByVertex: Uint32Array;
  /**
   * instanceIndex -> flat triangle-vertex-index list, precomputed once here rather than
   * rescanned per click: a selection highlight needs "every triangle belonging to instance X",
   * and a real building can have hundreds of thousands of triangles, so re-deriving that by
   * scanning the whole model on every click would be O(clicks x triangles) instead of the
   * O(triangles) this pays once at import.
   */
  trianglesByInstance: ReadonlyMap<number, number[]>;
};

export type ImportedIfcModel = {
  sourceFileName: string;
  sourceBytes: number;
  sourceSha256: string;
  elements: ImportedIfcElement[];
  chunks: ImportedChunk[];
  /** instanceIndex -> ImportedIfcElement.id, for resolving a pick into an identity. */
  elementIdByInstance: ReadonlyMap<number, string>;
  storeys: ImportedIfcStorey[];
  triangleCount: number;
  chunkCount: number;
};

async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function importIfcFile(
  file: File,
  onProgress?: (message: string) => void,
): Promise<ImportedIfcModel> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const sourceSha256 = await sha256Hex(bytes);
  const source = new WasmMeshSource();
  const handle = await source.open(bytes);
  try {
    const manifest = await source.manifest(handle);
    const elementInfo = await source.elementInfo(handle);

    const elementsById = new Map<string, ImportedIfcElement>();
    const elementIdByInstance = new Map<number, string>();
    // Per-instance running bbox, accumulated across chunks without keeping any per-instance
    // geometry — cheap, and (unlike a "read bbox off whichever chunk happened to be processed
    // last" approach) not silently limited to a partial view of an element split across chunks.
    const boundsByInstance = new Map<number, THREE.Box3>();
    const chunks: ImportedChunk[] = [];
    const storeyElements = new Map<number, { name: string | null; ids: Set<string> }>();
    let triangleCount = 0;

    for (const manifestChunk of manifest.chunks) {
      onProgress?.(`Chunk ${manifestChunk.chunk_id + 1} / ${manifest.chunks.length}`);
      const raw = await source.chunk(handle, manifestChunk.chunk_id);
      const header = decodeChunkHeader(raw.header);
      const chunkHeader = header.chunks[0];
      const guidTable = header.guid_table;
      triangleCount += chunkHeader.triangle_count;

      const vertexCount = chunkHeader.vertices.count;
      const positions = new Float32Array(vertexCount * 3);
      const instanceByVertex = new Uint32Array(vertexCount);
      const [originX, originY, originZ] = chunkHeader.quant_origin_mm;
      const scale = chunkHeader.quant_scale_mm;
      const vertexView = new DataView(raw.blob.buffer, raw.blob.byteOffset + chunkHeader.vertices.byte_offset);
      const point = new THREE.Vector3();
      for (let i = 0; i < vertexCount; i++) {
        const base = i * VERTEX_STRIDE_BYTES;
        const worldXMm = originX + vertexView.getUint16(base, true) * scale;
        const worldYMm = originY + vertexView.getUint16(base + 2, true) * scale;
        const worldZMm = originZ + vertexView.getUint16(base + 4, true) * scale;
        const x = worldXMm / 1000;
        const y = worldYMm / 1000;
        const z = worldZMm / 1000;
        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;
        const instance = vertexView.getUint32(base + 8, true);
        instanceByVertex[i] = instance;

        let bounds = boundsByInstance.get(instance);
        if (!bounds) {
          bounds = new THREE.Box3();
          boundsByInstance.set(instance, bounds);
        }
        bounds.expandByPoint(point.set(x, y, z));
      }

      const indexView = new DataView(raw.blob.buffer, raw.blob.byteOffset + chunkHeader.indices.byte_offset);
      const indexCount = chunkHeader.indices.count;
      const indices = chunkHeader.indices.index_type === "u16" ? new Uint16Array(indexCount) : new Uint32Array(indexCount);
      for (let i = 0; i < indexCount; i++) {
        indices[i] = chunkHeader.indices.index_type === "u16" ? indexView.getUint16(i * 2, true) : indexView.getUint32(i * 4, true);
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));
      geometry.computeVertexNormals();

      // One pass over this chunk's index buffer, once, at import time.
      const trianglesByInstance = new Map<number, number[]>();
      for (let t = 0; t + 2 < indices.length; t += 3) {
        const a = indices[t];
        const instance = instanceByVertex[a];
        let list = trianglesByInstance.get(instance);
        if (!list) {
          list = [];
          trianglesByInstance.set(instance, list);
        }
        list.push(a, indices[t + 1], indices[t + 2]);
      }
      chunks.push({ chunkId: manifestChunk.chunk_id, geometry, instanceByVertex, trianglesByInstance });

      const level = manifestChunk.storey_name ?? `Storey ${manifestChunk.storey_level}`;
      if (!storeyElements.has(manifestChunk.storey_level)) {
        storeyElements.set(manifestChunk.storey_level, { name: manifestChunk.storey_name, ids: new Set() });
      }
      const storeyBucket = storeyElements.get(manifestChunk.storey_level)!;

      for (const instance of new Set(instanceByVertex)) {
        const identity = guidTable[instance] ?? `INSTANCE-${instance}`;
        elementIdByInstance.set(instance, identity);
        storeyBucket.ids.add(identity);
        if (!elementsById.has(identity)) {
          const info = elementInfo?.element_info[instance] ?? null;
          elementsById.set(identity, {
            id: identity,
            name: info?.name || info?.ifc_class || identity,
            ifcClass: info?.ifc_class ?? null,
            level,
            position: [0, 0, 0],
            size: [0.02, 0.02, 0.02],
          });
        }
      }
    }

    // Fill in position/size from the accumulated per-instance bounds now that every chunk has
    // been seen — this is why bounds accumulate separately from the geometry above rather than
    // being read off of one chunk's partial view of a possibly chunk-split element.
    for (const [instance, identity] of elementIdByInstance) {
      const element = elementsById.get(identity);
      const bounds = boundsByInstance.get(instance);
      if (!element || !bounds || bounds.isEmpty()) continue;
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      bounds.getCenter(center);
      bounds.getSize(size);
      element.position = [center.x, center.y, center.z];
      element.size = [Math.max(size.x, 0.02), Math.max(size.y, 0.02), Math.max(size.z, 0.02)];
    }

    const storeys: ImportedIfcStorey[] = Array.from(storeyElements.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, bucket]) => ({ level, name: bucket.name, elementCount: bucket.ids.size }));

    return {
      sourceFileName: file.name,
      sourceBytes: bytes.byteLength,
      sourceSha256,
      elements: Array.from(elementsById.values()),
      chunks,
      elementIdByInstance,
      storeys,
      triangleCount,
      chunkCount: manifest.chunks.length,
    };
  } finally {
    await source.close(handle);
  }
}
